import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';
import { publicAnonKey as supabaseAnonKey, supabaseUrl } from '../../utils/supabase/runtime';

export const isSupabaseConfigured = true;

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Bypass navigator.locks so a lock held by a closed tab or a crashed
      // browser extension can't leave getSession() hanging forever.
      lock: async (_name, _acquireTimeout, fn) => fn(),
    },
    realtime: {
      params: {
        eventsPerSecond: 10,
      },
    },
  }
);

// Auth helpers
export const auth = {
  signUp: async (email: string, password: string, metadata?: Record<string, any>) => {
    return supabase.auth.signUp({
      email,
      password,
      phone: metadata?.phone, // Store phone in auth table's phone column
      options: {
        data: metadata, // Also store in user_metadata
        emailRedirectTo: undefined, // Skip email confirmation redirect
      },
    });
  },

  signIn: async (email: string, password: string) => {
    return supabase.auth.signInWithPassword({
      email,
      password,
    });
  },

  signOut: async () => {
    return supabase.auth.signOut();
  },

  resetPassword: async (email: string) => {
    return supabase.auth.resetPasswordForEmail(email);
  },

  updatePassword: async (newPassword: string) => {
    return supabase.auth.updateUser({
      password: newPassword,
    });
  },

  getSession: async () => {
    return supabase.auth.getSession();
  },

  getUser: async () => {
    return supabase.auth.getUser();
  },

  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// Storage helpers
export const storage = {
  uploadFile: async (bucket: string, path: string, file: File) => {
    return supabase.storage.from(bucket).upload(path, file);
  },

  getPublicUrl: (bucket: string, path: string) => {
    return supabase.storage.from(bucket).getPublicUrl(path);
  },

  deleteFile: async (bucket: string, path: string) => {
    return supabase.storage.from(bucket).remove([path]);
  },
};

// Realtime helpers
export const realtime = {
  subscribe: (channel: string, table: string, callback: (payload: any) => void) => {
    return supabase
      .channel(channel)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table },
        callback
      )
      .subscribe();
  },

  unsubscribe: (channel: any) => {
    return supabase.removeChannel(channel);
  },
};

export default supabase;
